import React from 'react';
import Icon from '../../../components/AppIcon';

const UsageAnalytics = ({ subscriptionData, detailed = false }) => {
  // Mock usage data
  const usageData = {
    members: {
      current: 1247,
      limit: 2000,
      percentage: 62
    },
    locations: {
      current: 3,
      limit: 5,
      percentage: 60
    },
    staff: {
      current: 18,
      limit: 25,
      percentage: 72
    },
    storage: {
      current: 12.4,
      limit: 50,
      percentage: 25,
      unit: 'GB'
    },
    apiCalls: {
      current: 8450,
      limit: 10000,
      percentage: 85
    }
  };

  const activityMetrics = [
    { label: 'Daily Active Users', value: '847', trend: 'up', change: '+12%' },
    { label: 'Monthly Check-ins', value: '18,450', trend: 'up', change: '+8%' },
    { label: 'Classes Scheduled', value: '156', trend: 'stable', change: '0%' },
    { label: 'Revenue This Month', value: '$42,350', trend: 'up', change: '+15%' }
  ];

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'red';
    if (percentage >= 75) return 'yellow';
    return 'green';
  };

  const UsageBar = ({ label, current, limit, percentage, unit = '', icon }) => {
    const color = getUsageColor(percentage);
    const colorClasses = {
      green: 'bg-green-500',
      yellow: 'bg-yellow-500',
      red: 'bg-red-500'
    };

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name={icon} className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-900">{label}</span>
          </div>
          <span className="text-sm text-gray-600">
            {current?.toLocaleString()} {unit} / {limit?.toLocaleString()} {unit}
          </span>
        </div>
        <div className="relative w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`absolute top-0 left-0 h-full ${colorClasses?.[color]} transition-all duration-300`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>
        <div className="flex items-center justify-between">
          <span className={`text-xs font-medium ${
            color === 'red' ? 'text-red-600' : color === 'yellow' ? 'text-yellow-600' : 'text-green-600'
          }`}>
            {percentage}% used
          </span>
          {percentage >= 90 && (
            <span className="text-xs text-red-600 font-medium">
              <Icon name="AlertTriangle" className="w-3 h-3 inline mr-1" />
              Approaching limit
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Usage Overview */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Platform Usage</h3>
        <div className="space-y-6">
          <UsageBar
            label="Members"
            current={usageData?.members?.current}
            limit={usageData?.members?.limit}
            percentage={usageData?.members?.percentage}
            icon="Users"
          />
          <UsageBar
            label="Gym Locations"
            current={usageData?.locations?.current}
            limit={usageData?.locations?.limit}
            percentage={usageData?.locations?.percentage}
            icon="MapPin"
          />
          <UsageBar
            label="Staff Accounts"
            current={usageData?.staff?.current}
            limit={usageData?.staff?.limit}
            percentage={usageData?.staff?.percentage}
            icon="UserCheck"
          />
          <UsageBar
            label="Storage"
            current={usageData?.storage?.current}
            limit={usageData?.storage?.limit}
            percentage={usageData?.storage?.percentage}
            unit={usageData?.storage?.unit}
            icon="HardDrive"
          />
          {detailed && (
            <UsageBar
              label="API Calls (Monthly)"
              current={usageData?.apiCalls?.current}
              limit={usageData?.apiCalls?.limit}
              percentage={usageData?.apiCalls?.percentage}
              icon="Zap"
            />
          )}
        </div>
      </div>

      {/* Activity Metrics */}
      {detailed && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Activity Metrics</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {activityMetrics?.map((metric, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600 mb-1">{metric?.label}</p>
                  <p className="text-2xl font-bold text-gray-900">{metric?.value}</p>
                </div>
                <div className="flex items-center gap-1">
                  <Icon
                    name={metric?.trend === 'up' ? 'TrendingUp' : metric?.trend === 'down' ? 'TrendingDown' : 'Minus'}
                    className={`w-5 h-5 ${
                      metric?.trend === 'up' ? 'text-green-600' : metric?.trend === 'down' ? 'text-red-600' : 'text-gray-600'
                    }`}
                  />
                  <span className={`text-sm font-medium ${
                    metric?.trend === 'up' ? 'text-green-600' : metric?.trend === 'down' ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {metric?.change}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Overage Alerts */}
      {usageData?.apiCalls?.percentage >= 85 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-yellow-900 mb-2">Usage Alert</h3>
              <p className="text-sm text-yellow-800 mb-3">
                You're approaching your API call limit for this billing period. Consider upgrading your plan 
                to avoid service interruptions.
              </p>
              <button className="text-sm font-medium text-yellow-900 hover:text-yellow-700 underline">
                View Upgrade Options
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Usage Tips */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-start gap-3">
          <Icon name="Lightbulb" className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Optimize Your Usage</h3>
            <ul className="text-sm text-gray-600 space-y-2">
              <li className="flex items-start gap-2">
                <Icon name="Check" className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Archive inactive members to free up capacity</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="Check" className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Compress images and files to reduce storage usage</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="Check" className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Review API integration settings to optimize call frequency</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsageAnalytics;